import type { FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Gap, GapSize } from '../../../shared/components/gap/gap';
import { TitledSection } from '../../../shared/components/titled-section/titled-section';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createStyleHelper } from '../../../shared/utils/class-names';
import type { IUserProfileFreeBet } from '../../graphql/query.graphql';
import { IUserProfileFreeBetStatus } from '../../graphql/query.graphql';
import { EmptyMessage } from '../empty-message/empty-message';
import { FreeBetCard } from '../free-bet-card/free-bet-card';
import { FreeBetHeading } from '../free-bet-heading/free-bet-heading';

import styles from './free-bets.module.scss';

const style = createStyleHelper(styles, 'free-bets');

export interface IUserFreeBetsProps {
    availableFreeBets: IUserProfileFreeBet[];
    inactiveFreeBets: IUserProfileFreeBet[];
}

export const FreeBetsOverview: FunctionComponent<IUserFreeBetsProps> = ({
    availableFreeBets,
    inactiveFreeBets,
}) => {
    const isFullDisplay = useDesktop();
    const intl = useIntl();

    return (
        <Gap size={GapSize.LARGE} className={style()}>
            <TitledSection
                title={intl.formatMessage({
                    id: 'account.free-bets.available-free-bets',
                    defaultMessage: 'Available Free Bets',
                })}
                description={
                    isFullDisplay
                        ? intl.formatMessage({
                              id: 'account.free-bets.available-description',
                              defaultMessage:
                                  'All currently active free bets that can be consumed.',
                          })
                        : undefined
                }
            >
                <FreeBetHeading />
                <Gap>
                    {availableFreeBets.length !== 0 ? (
                        availableFreeBets.map((item) => {
                            const content = item.content;
                            return (
                                <FreeBetCard
                                    key={item.id}
                                    id={item.id ?? ''}
                                    name={item.name ?? ''}
                                    currency={item.currencyCode ?? ''}
                                    expiresOn={item.expiresOn ?? ''}
                                    grantedAmount={item.grantedAmount ?? 0}
                                    consumedGrantedAmount={item.consumedGrantedAmount ?? 0}
                                    balanceAmount={item.balanceAmount ?? 0}
                                    conversionMaxAmount={item.conversionMaxAmount ?? 0}
                                    convertedAmount={item.convertedAmount ?? 0}
                                    status={item.status ?? IUserProfileFreeBetStatus.Cancelled}
                                    mediatorId={item.mediatorId ?? ''}
                                    title={content?.title ?? ''}
                                    subtitle={content?.subtitle ?? ''}
                                    description={content?.description ?? ''}
                                    image={content?.image ?? ''}
                                />
                            );
                        })
                    ) : (
                        <EmptyMessage
                            title={
                                <FormattedMessage
                                    id="account.free-bets.available-free-bets.empty.title"
                                    defaultMessage={'No available free bets'}
                                />
                            }
                            message={
                                <FormattedMessage
                                    id="account.free-bets.available-free-bets.empty.message"
                                    defaultMessage={
                                        'You do not currently have any available free bets. Check back later!'
                                    }
                                />
                            }
                        />
                    )}
                </Gap>
            </TitledSection>

            <TitledSection
                title={intl.formatMessage({
                    id: 'account.free-bets.inactive-free-bets',
                    defaultMessage: 'Past Free bets',
                })}
                description={
                    isFullDisplay
                        ? intl.formatMessage({
                              id: 'account.free-bets.inactive-description',
                              defaultMessage: 'List of all previous free bets',
                          })
                        : undefined
                }
            >
                <FreeBetHeading />
                <Gap>
                    {inactiveFreeBets.length !== 0 ? (
                        inactiveFreeBets.map((item) => {
                            const content = item.content;
                            return (
                                <FreeBetCard
                                    key={item.id}
                                    id={item.id ?? ''}
                                    name={item.name ?? ''}
                                    currency={item.currencyCode ?? ''}
                                    expiresOn={item.expiresOn ?? ''}
                                    grantedAmount={item.grantedAmount ?? 0}
                                    consumedGrantedAmount={item.consumedGrantedAmount ?? 0}
                                    balanceAmount={item.balanceAmount ?? 0}
                                    conversionMaxAmount={item.conversionMaxAmount ?? 0}
                                    convertedAmount={item.convertedAmount ?? 0}
                                    status={item.status ?? IUserProfileFreeBetStatus.Cancelled}
                                    mediatorId={item.mediatorId ?? ''}
                                    title={content?.title ?? ''}
                                    subtitle={content?.subtitle ?? ''}
                                    description={content?.description ?? ''}
                                    image={content?.image ?? ''}
                                />
                            );
                        })
                    ) : (
                        <EmptyMessage
                            title={
                                <FormattedMessage
                                    id="account.free-bets.inactive-free-bets.empty.title"
                                    defaultMessage={'No inactive free bets'}
                                />
                            }
                            message={
                                <FormattedMessage
                                    id="account.free-bets.inactive-free-bets.empty.message"
                                    defaultMessage={
                                        'You do not currently have any inactive free bets.'
                                    }
                                />
                            }
                        />
                    )}
                </Gap>
            </TitledSection>

            <p className={style('footer')}>
                <FormattedMessage
                    id="account.free-bets.footer-description"
                    defaultMessage="Free bets are assigned to you by the system and can not be claimed."
                />
            </p>
        </Gap>
    );
};
